/* eslint-disable max-lines */
import { parseFrameData, parseSseStream } from './parseSseStream'
import { initialPhase1RunState, streamEventReducer } from './streamEventReducer'
import { parseSseEvent } from '@/schemas/contracts/sseEvent'
import { postChatStream } from '@/services/chatStream/postChatStream'
import { attachmentsFromFiles, docScopeFromFiles } from './phase1StreamPayload'
import { patchAssistant, patchFromRun } from './phase1StreamPatch'
import { patchPhase1Error, patchPhase1Interrupted } from './phase1StreamError'
import { settlePhase1Stream } from './phase1StreamCompletion'
import { useThreadStatusStore } from '@/store/threadStatusStore'

export function runPhase1ChatStream(ctx) {
  const { tid, trimmed, files = [], proposalId, researchMode, refinement, model, signal, session } = ctx
  // A clarification submission opened its own stream against its own endpoint,
  // and did so before this turn existed. Everything after the response is
  // identical, so only the call that produces it is swappable.
  const open = ctx.openStream
  let run = initialPhase1RunState()

  patchAssistant(ctx, {
    text: '',
    responseParts: [],
    blocks: [],
    activity: [],
    workStatus: null,
    publicTimeline: [],
    citations: [],
    memoryIds: [],
    demoStreamActive: true,
    thoughtSummary: 'Starting stream...',
  })
  ctx.onChatEvent?.({ type: 'client_timing', name: 'api_request_start', timestamp_ms: Date.now() })
  useThreadStatusStore.getState().setGenerating(tid, true)

  void (open
    ? Promise.resolve(open())
    : postChatStream(
        {
          conversationId: tid,
          content: trimmed,
          docScope: docScopeFromFiles(files),
          attachments: attachmentsFromFiles(files),
          proposalId,
          researchMode,
          refinement,
          model,
        },
        signal,
      ))
    .then((response) => {
      ctx.onChatEvent?.({ type: 'client_timing', name: 'sse_start', timestamp_ms: Date.now() })
      return parseSseStream(
        response.body,
        ({ data }) => {
          const raw = parseFrameData(data)
          if (!raw) return
          const event = parseSseEvent(raw)
          run = streamEventReducer(run, event)
          patchFromRun(ctx, run)
          ctx.onChatEvent?.(event)
        },
        { signal },
      )
    })
    .then(() => {
      settlePhase1Stream(ctx, run)
    })
    .catch((error) => {
      if (signal.aborted) {
        patchPhase1Interrupted(ctx, run)
        return
      }
      // A browser-side read failure does not mean the server-side turn failed:
      // the API keeps it running in the background. Once `start` gave us a run
      // id, hand the existing recovery path that durable identity instead of
      // replacing the pending reply with a terminal-looking network error.
      if (run.runId) {
        settlePhase1Stream(ctx, run)
        return
      }
      patchPhase1Error(ctx, error, run)
    })
    .finally(() => {
      ctx.onChatEvent?.({ type: 'stream_settled' })
      session.dispose()
      ctx.onSettled?.()
      const store = useThreadStatusStore.getState()
      store.setGenerating(tid, false)
      if (typeof window !== 'undefined' && !window.location.pathname.includes(tid)) {
        store.markUnread(tid)
      }
    })
}
